"use client";

import { useSearchParams, useRouter } from "next/navigation";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";
import { formatPrice } from "@/lib/utils";
import { useLocale, useTranslations } from "next-intl";

export default function FiltersBadges({ categories }) {
  const t = useTranslations("shopProductFilters");
  const locale = useLocale();
  const searchParams = useSearchParams();
  const router = useRouter();

  const sortOptions = {
    "price-asc": t("priceAscending"),
    "price-desc": t("priceDescending"),
    "best-rating": t("rating"),
    new: t("recent"),
    "a-z": t("alphaAscending"),
    "z-a": t("alphaDescending"),
  };

  const removeFilter = (key) => {
    const newParams = new URLSearchParams(searchParams.toString());
    if (key === "price") {
      newParams.delete("minPrice");
      newParams.delete("maxPrice");
    } else {
      newParams.delete(key);
    }
    router.push(`?${newParams.toString()}`);
  };

  const resetFilters = () => {
    router.push(window.location.pathname);
  };

  const formatFilter = (key, value) => {
    switch (key) {
      case "price":
        const minPrice = searchParams.get("minPrice") || "0";
        const maxPrice = searchParams.get("maxPrice") || "∞";
        return `Prix : ${formatPrice(minPrice, locale, false)} - ${formatPrice(maxPrice, locale, false)}`;
      case "categories":
        const categoryIds = value.split(",").map(Number);
        const categoryNames = categories
          .filter((cat) => categoryIds.includes(cat.id_category))
          .map((cat) =>
            locale === "fr"
              ? cat.category_name
              : locale === "ar"
                ? cat.category_name_ar
                : cat.category_name_en,
          );
        return categoryNames.length > 3
          ? `${t("categories")} : ${categoryNames.length} sélectionnées`
          : `${t("categories")} : ${categoryNames.join(", ")}`;
      case "sort":
        return `${t("sort")} : ${sortOptions[value] || value}`;
      case "search":
        return `${t("search")} : ${value}`;
      case "inStockOnly":
        return `En stock seulement`;
      case "inPromoOnly":
        return `En promotion seulement`;
      default:
        return `${key}: ${value}`;
    }
  };

  const getAppliedFilters = () => {
    const filters = [];
    for (const [key, value] of searchParams.entries()) {
      if (key === "minPrice" || key === "maxPrice") {
        if (!filters.some((f) => f[0] === "price")) {
          filters.push(["price", ""]);
        }
      } else {
        filters.push([key, value]);
      }
    }
    return filters;
  };

  const appliedFilters = getAppliedFilters();

  return (
    <div className="my-2.5 flex flex-wrap items-center gap-2">
      {appliedFilters.map(([key, value]) => (
        <Badge key={key} variant="secondary" className="px-2 py-1">
          {formatFilter(key, value)}
          <Button
            variant="ghost"
            size="sm"
            className="ml-2 h-4 w-4 p-0"
            onClick={() => removeFilter(key)}
          >
            <X className="h-3 w-3" />
          </Button>
        </Badge>
      ))}
      {appliedFilters.length > 0 && (
        <Button variant="ghost" size="sm" onClick={resetFilters}>
          <X className="mr-2" /> {t("resetFilters")}
        </Button>
      )}
    </div>
  );
}
